import { useEffect, useState } from "react"
import {
  BookOpen, AlertTriangle, Loader2,
  CheckCircle, Zap, Award,
} from "lucide-react"
import { API_URL } from '@/services/apiClient'

interface ActivityItem {
  id: string
  description: string
  type: string
  points?: number
  severity?: string
  createdAt: string
}

interface ActivitiesData {
  digital: ActivityItem[]
  classroom: ActivityItem[]
  reported: ActivityItem[]
  summary: {
    totalEvents: number
    flaggedEvents: number
    safeEvents: number
    classroomActivities: number
    alertCount: number
  }
}

type Tab = 'digital' | 'classroom' | 'alerts'

export function StudentClassroom() {
  const [data, setData]           = useState<ActivitiesData | null>(null)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('classroom')
  const [dm, setDm]               = useState(() => localStorage.getItem('studentDarkMode') === 'true')

  useEffect(() => {
    const handler = (e: Event) => setDm((e as CustomEvent<{ darkMode: boolean }>).detail.darkMode)
    window.addEventListener('studentDarkModeChanged', handler)
    return () => window.removeEventListener('studentDarkModeChanged', handler)
  }, [])

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const token = sessionStorage.getItem('authToken')
        const res = await fetch(`${API_URL}/student/activities`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (res.ok) {
          const json = await res.json()
          setData(json.activities)
        } else {
          setError('Failed to load activities')
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }
    fetchActivities()
  }, [])

  const glassCard = dm
    ? "bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 shadow-md"
    : "bg-white/75 backdrop-blur-sm rounded-2xl border border-white/60 shadow-md"

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/60 backdrop-blur-sm border border-white/60 flex items-center justify-center animate-pulse">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
          <span className={`text-sm ${dm ? 'text-white/60' : 'text-gray-600'}`}>Loading activities…</span>
        </div>
      </div>
    )
  }

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'digital',   label: 'Digital',   count: data?.digital?.length   ?? 0 },
    { id: 'classroom', label: 'Classroom', count: data?.classroom?.length ?? 0 },
    { id: 'alerts',    label: 'Alerts',    count: data?.reported?.length  ?? 0 },
  ]

  // ── Classroom / Digital activity row ────────────────────────────────────
  const ActivityRow = ({ activity }: { activity: ActivityItem }) => {
    const isSelfCorrection = activity.type === 'resolved'
    const isGame = activity.description?.toLowerCase().startsWith('completed game:')

    const iconBg = isSelfCorrection
      ? dm ? 'bg-emerald-900/40' : 'bg-emerald-100/80'
      : isGame
      ? dm ? 'bg-indigo-900/40'  : 'bg-indigo-100/80'
      : dm ? 'bg-amber-900/40'   : 'bg-amber-100/80'
    const Icon      = isSelfCorrection ? CheckCircle : isGame ? Zap : Award
    const iconColor = isSelfCorrection ? 'text-emerald-500' : isGame ? 'text-indigo-500' : 'text-amber-500'

    return (
      <div className={`flex items-center gap-3 p-3 rounded-xl backdrop-blur-sm border shadow-sm ${
        dm ? 'bg-white/10 border-white/10' : 'bg-white/60 border-white/60'
      }`}>
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${dm ? 'text-white' : 'text-gray-900'}`}>{activity.description}</p>
          <p className={`text-xs mt-0.5 ${dm ? 'text-white/40' : 'text-gray-500'}`}>{formatTime(activity.createdAt)}</p>
        </div>
        {activity.points !== undefined && activity.points !== null && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 text-emerald-600 bg-emerald-100/80">
            +{activity.points}
          </span>
        )}
      </div>
    )
  }

  // ── Alert row (system-detected content) ─────────────────────────────────
  const AlertRow = ({ activity }: { activity: ActivityItem }) => {
    const borderBg = dm
      ? activity.severity === 'critical' ? 'border-rose-500/40   bg-rose-900/30'
      : activity.severity === 'high'     ? 'border-rose-400/30   bg-rose-900/20'
      : activity.severity === 'medium'   ? 'border-amber-400/30  bg-amber-900/20'
      :                                    'border-white/10       bg-white/10'
      : activity.severity === 'critical' ? 'border-rose-400/60   bg-rose-50/70'
      : activity.severity === 'high'     ? 'border-rose-300/60   bg-rose-50/60'
      : activity.severity === 'medium'   ? 'border-amber-300/60  bg-amber-50/60'
      :                                    'border-gray-200/60    bg-white/60'

    return (
      <div className={`rounded-2xl border ${borderBg} backdrop-blur-sm p-4`}>
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${dm ? 'bg-rose-900/50' : 'bg-rose-100/80'}`}>
            <AlertTriangle className="w-4 h-4 text-rose-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className={`text-sm font-semibold ${dm ? 'text-white' : 'text-gray-800'}`}>System detected content</p>
              {activity.severity && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  activity.severity === 'critical' ? 'bg-rose-200   text-rose-800'   :
                  activity.severity === 'high'     ? 'bg-rose-100   text-rose-700'   :
                  activity.severity === 'medium'   ? 'bg-amber-100  text-amber-700'  :
                                                     'bg-gray-100   text-gray-600'
                }`}>
                  {activity.severity.toUpperCase()}
                </span>
              )}
            </div>
            <p className={`text-xs leading-relaxed ${dm ? 'text-white/70' : 'text-gray-600'}`}>{activity.description}</p>
            <p className={`text-[10px] mt-1.5 ${dm ? 'text-white/40' : 'text-gray-400'}`}>{formatTime(activity.createdAt)}</p>
          </div>
        </div>
      </div>
    )
  }

  // ── Empty states ─────────────────────────────────────────────────────────
  const EmptyState = ({ tab }: { tab: Tab }) => {
    if (tab === 'digital') return (
      <div className="text-center py-12">
        <div className={`w-14 h-14 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 ${dm ? 'bg-emerald-900/40 border border-emerald-500/20' : 'bg-emerald-50/80 border border-emerald-200/60'}`}>
          <CheckCircle className="w-7 h-7 text-emerald-400" />
        </div>
        <p className={`font-semibold ${dm ? 'text-white' : 'text-gray-700'}`}>No self-corrections yet</p>
        <p className={`text-xs mt-1 max-w-[220px] mx-auto leading-relaxed ${dm ? 'text-white/50' : 'text-gray-400'}`}>
          When you correct flagged content yourself, it'll appear here as a positive moment
        </p>
      </div>
    )
    if (tab === 'classroom') return (
      <div className="text-center py-12">
        <div className={`w-14 h-14 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 ${dm ? 'bg-indigo-900/40 border border-indigo-500/20' : 'bg-indigo-50/80 border border-indigo-200/60'}`}>
          <BookOpen className="w-7 h-7 text-indigo-400" />
        </div>
        <p className={`font-semibold ${dm ? 'text-white' : 'text-gray-700'}`}>No classroom activity yet</p>
        <p className={`text-xs mt-1 ${dm ? 'text-white/50' : 'text-gray-400'}`}>Play games or earn teacher rewards to see points here</p>
      </div>
    )
    return (
      <div className="text-center py-12">
        <div className={`w-14 h-14 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 ${dm ? 'bg-emerald-900/40 border border-emerald-500/20' : 'bg-emerald-100/80 border border-emerald-200/60'}`}>
          <CheckCircle className="w-7 h-7 text-emerald-500" />
        </div>
        <p className="font-semibold text-emerald-500">No safety alerts!</p>
        <p className={`text-xs mt-1 ${dm ? 'text-white/50' : 'text-gray-500'}`}>Keep up the great work</p>
      </div>
    )
  }

  const currentItems =
    activeTab === 'digital'   ? (data?.digital   ?? []) :
    activeTab === 'classroom' ? (data?.classroom ?? []) :
                                (data?.reported  ?? [])

  return (
    <div className="px-4 py-5 space-y-4">
      {error && (
        <div className="bg-amber-50/80 backdrop-blur-sm border border-amber-200/70 text-amber-700 px-4 py-3 rounded-2xl text-sm">
          Using cached data — live sync unavailable
        </div>
      )}

      {/* ── Summary stats ── */}
      {data?.summary && (
        <div className="grid grid-cols-3 gap-3">
          <div className={`backdrop-blur-sm rounded-2xl p-3 text-center ${dm ? 'bg-emerald-900/40 border border-emerald-500/20' : 'bg-emerald-50/80 border border-emerald-200/60'}`}>
            <div className="text-2xl font-bold text-emerald-500">{data.summary.safeEvents}</div>
            <div className={`text-xs font-medium mt-0.5 ${dm ? 'text-emerald-400' : 'text-emerald-600'}`}>Safe</div>
          </div>
          <div className={`backdrop-blur-sm rounded-2xl p-3 text-center ${dm ? 'bg-rose-900/40 border border-rose-500/20' : 'bg-rose-50/80 border border-rose-200/60'}`}>
            <div className="text-2xl font-bold text-rose-500">{data.summary.flaggedEvents}</div>
            <div className={`text-xs font-medium mt-0.5 ${dm ? 'text-rose-400' : 'text-rose-600'}`}>Flagged</div>
          </div>
          <div className={`backdrop-blur-sm rounded-2xl p-3 text-center ${dm ? 'bg-indigo-900/40 border border-indigo-500/20' : 'bg-indigo-50/80 border border-indigo-200/60'}`}>
            <div className="text-2xl font-bold text-indigo-500">{data.summary.totalEvents}</div>
            <div className={`text-xs font-medium mt-0.5 ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>Total</div>
          </div>
        </div>
      )}

      {/* ── Separator ── */}
      <hr className={`border-t ${dm ? 'border-white/10' : 'border-gray-200/60'}`} />

      {/* ── Tab bar ── */}
      <div className={`backdrop-blur-sm rounded-2xl p-1 flex gap-1 ${dm ? 'bg-white/10 border border-white/10' : 'bg-white/50 border border-white/60'}`}>
        {tabs.map(({ id, label, count }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
              activeTab === id
                ? dm ? 'bg-white/20 text-white shadow-sm' : 'bg-white/90 text-gray-900 shadow-sm'
                : dm ? 'text-white/50 hover:text-white/80' : 'text-gray-500 hover:text-gray-700'
            }`}
            style={{ border: 'none', background: activeTab === id ? undefined : 'transparent' }}
          >
            {label}
            {count > 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                activeTab === id
                  ? id === 'alerts' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'
                  : dm ? 'bg-white/10 text-white/50' : 'bg-gray-200/80 text-gray-500'
              }`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab context banners ── */}
      {activeTab === 'digital' && (
        <div className={`${glassCard} px-4 py-3 flex items-start gap-3`}>
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
          <p className={`text-xs leading-relaxed ${dm ? 'text-white/70' : 'text-gray-600'}`}>
            <span className={`font-semibold ${dm ? 'text-white' : 'text-gray-700'}`}>Digital moments</span> times you showed good judgement online: self-correcting flagged content, choosing safer alternatives, and demonstrating better digital citizenship.
          </p>
        </div>
      )}
      {activeTab === 'classroom' && (
        <div className={`${glassCard} px-4 py-3 flex items-start gap-3`}>
          <BookOpen className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
          <p className={`text-xs leading-relaxed ${dm ? 'text-white/70' : 'text-gray-600'}`}>
            <span className={`font-semibold ${dm ? 'text-white' : 'text-gray-700'}`}>Classroom points</span> points earned from completing safety games and teacher rewards.
          </p>
        </div>
      )}
      {activeTab === 'alerts' && (
        <div className={`backdrop-blur-sm rounded-2xl p-4 ${dm ? 'bg-amber-900/30 border border-amber-500/20' : 'bg-amber-50/80 border border-amber-100/80'}`}>
          <p className={`text-sm font-semibold mb-1 ${dm ? 'text-amber-300' : 'text-amber-800'}`}>What are safety alerts?</p>
          <p className={`text-xs leading-relaxed ${dm ? 'text-amber-400' : 'text-amber-700'}`}>
            Content is flagged when our system detects potentially harmful language like cyberbullying, threats, or self-harm. This isn't about getting in trouble. It's here to help you stay safe and make better choices online.
          </p>
        </div>
      )}

      {/* ── Content list ── */}
      <div className="space-y-2">
        {currentItems.length > 0
          ? currentItems.map(a =>
              activeTab === 'alerts'
                ? <AlertRow key={a.id} activity={a} />
                : <ActivityRow key={a.id} activity={a} />
            )
          : <EmptyState tab={activeTab} />
        }
      </div>

      <div className="h-2" />
    </div>
  )
}
